import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { rangeFromStored, type VisibleRange } from "../lib/visibleRange";
import { wireNumber } from "../lib/wireNumber";

/**
 * W16's value, read on its OWN so the typed query above stays honest.
 *
 * 🔴 IT WAS A HAND-WRITTEN CAST FOR ONE MORNING, AND THE CAST IS GONE. `database.types.ts` is
 * GENERATED FROM THE LIVE DATABASE, and `visible_range` was not in it: naming a not-yet-existing
 * column in a typed select turns the WHOLE ROW into `SelectQueryError` and eleven unrelated
 * fields stop compiling, so the query was written through a structural cast and allowed to fail.
 * That let the screen ship BEFORE the migration instead of waiting behind it. The column landed
 * at 09:10 and the types were regenerated, so this is an ordinary typed read again.
 *
 * ⚠️ IT STAYS A SEPARATE READ. Folding it into the main `.select(…)` would be free today and
 * would make the NEXT column added here a compile error across eleven fields — the shape this
 * function exists to avoid. One small query, one failure that cannot spread.
 *
 * ⚠️ AND A FAILURE HERE IS NOT A FAILURE OF THE PAGE. The client screen is what the coach came
 * for; a missing visibility setting is a missing annotation. It never calls `setError` — the
 * control says the setting could not be read.
 */
async function readVisibleRange(linkId: string): Promise<VisibleRange | undefined> {
  const { data, error } = await supabase
    .from("coach_clients").select("visible_range").eq("id", linkId).limit(1);
  const row = data?.[0];
  if (error !== null || row === undefined) return undefined;
  return rangeFromStored(row.visible_range);
}
/**
 * The coach's private note on this relationship — one row, read by the LINK's id.
 *
 * ⚠️ THE ABSENT ROW IS THE NORMAL CASE, AND THAT IS WHY THIS IS A **LIST**, NOT `maybeSingle`.
 * A coach who has never written a note has no row — `coach_client_notes` is written on first save
 * and never seeded, so "two policies, zero rows" is exactly what a freshly-applied migration
 * looks like. `maybeSingle` answers a no-row read with a 406 and a message the client has to
 * pattern-match to turn back into "nothing there"; a one-element list answers it with an empty
 * array, and an empty array cannot be mistaken for a failure. The commonest state deserves the
 * cheapest reading.
 *
 * ⚠️ AND A FAILURE IS RETURNED AS `undefined`, NEVER AS AN EMPTY NOTE. `undefined` is "not
 * loaded" to the card and it draws its loading line; an empty `CoachNote` would be a box a coach
 * could type into and lose, which is the one outcome a private-notes card must not have. It
 * never calls `setError` — the client screen is what the coach came for and a missing note is a
 * missing annotation.
 */
async function readCoachNote(linkId: string): Promise<CoachNote | undefined> {
  const { data, error } = await supabase
    .from("coach_client_notes").select("body, updated_at")
    .eq("coach_client_id", linkId).limit(1);
  if (error !== null || data === null) return undefined;
  const row = data[0];
  if (row === undefined) return { linkId, body: "", updatedAtISO: null };
  return { linkId, body: row.body, updatedAtISO: row.updated_at };
}

import { href, routeQuery, type Route } from "../lib/route";
import { WorkoutBuilder } from "./WorkoutBuilder";
import { useQuerySort } from "../lib/useQuerySort";
import { dayKeyOf, dayKey, shiftDayKey, calendarToday, coachZone } from "../lib/days";
import { useReminders } from "./useReminders";
import { useClientWatch } from "./useClientWatch";
import { watchItemLine } from "../lib/reviewQueue";
import { ReminderWindow, type ReminderTarget } from "../components/ReminderWindow";
import { useCalls } from "./useCalls";
import { CALLS_INCLUDED } from "../lib/calls";
import { CallWindow, ClientCalls, type CallTarget } from "../components/Calls";
import { CallHost } from "../components/CallScreen";
import { mondayKey, shortDate, weekCells, type WeekCell } from "../lib/weekLine";
import { withPicker } from "../lib/assignPicker";
import { CLIENT_TABS, clientQuery, tabFromQuery, withDevTabAlias, type ClientTab } from "../lib/clientTabs";
import { PHONE_GROUP_LABEL, PHONE_STRIP, phoneGroup, tabForGroup } from "../lib/phoneClientNav";
import { ClientTraining } from "./ClientTraining";
import { type Measurement } from "../lib/bodyMetrics";
import { SET_DAY_SELECT } from "../lib/rosterData";
import { ClientProgress } from "./ClientProgress";
import { ClientProfile } from "./ClientProfile";
import { weekSummaryLine } from "../lib/appRoster";
import { CoachNotesCard } from "../components/CoachNotesCard";
import { readInjuries, type InjuryLine } from "../lib/injuries";
import { COACH_NOTES_ENABLED, type CoachNote } from "../lib/coachNotes";
import { TaskEditor } from "./TaskEditor";
import { Avatar } from "../components/Avatar";
import { NO_NEXT_WEEK_LINE, overlayOn, overlayPairs } from "../lib/proposalOverlay";
import { WeekLine } from "../components/WeekLine";
import { readNextWeekChanges } from "../lib/nextWeekProposal";
import { shiftConfirm, shiftPlan } from "../lib/shiftBlock";
import { rescheduleAssignment, type AssignmentWriter } from "../lib/assignmentActions";
import { Workspace } from "../components/Shell";
import { displayName } from "../lib/roster";
import { useReturnTick } from "../lib/useReturnTick";
import {
  outcome,
  toAssignments,
  toLoggedSets,
  setsPerTrainedDay,
  trainingDays,
  windows,
  type Assignment,
} from "../lib/client";

/* The client detail page — `EVERFIT-MAP.md` §2, "where a coach actually works".
 *
 * The roster answers *who* needs the coach today; this answers *what about them*. It is also the
 * screen that earned the router: the first thing you can be on while signed in that is not the
 * roster.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 TWO PANELS §2 SPECIFIES ARE DELIBERATELY NOT BUILT, AND THE REASON IS RLS RATHER THAN TIME.
 *
 * Measured per identity before a line of this screen was written, which is the same check that
 * made the roster's "last trained" column trustworthy:
 *
 *   | table                | rows the COACH can read | rows that EXIST for these two clients |
 *   |----------------------|-------------------------|---------------------------------------|
 *   | `workout_sessions`   | 118                     | 118                                   |
 *   | `set_logs`           | 349                     | 349                                   |
 *   | `profiles`           | 3 of 14                 | —                                     |
 *   | **`body_measurements`** | **0**                | **2**                                 |
 *   | **`engine_events`**  | **0**                   | **164**                               |
 *
 * `body_measurements` has no coach policy at all — only `auth.uid() = user_id`. `engine_events`
 * has only the owner's and the beta admin's. So §2's **body-metrics panel** and §2a's **updates
 * feed** would each render an honest-looking "No data available" over data that exists and is
 * merely refused.
 *
 * ⚠️ THAT IS THE EXACT FAILURE THE ROSTER NARROWLY AVOIDED. Had the coach been unable to read
 * `workout_sessions`, every roster row would have said "has never trained" and looked completely
 * plausible: **a silently empty column is indistinguishable from a client who quit.** So neither
 * panel is built, and in place of each the screen says WHICH THING IS MISSING AND WHY.
 *
 * Widening either policy is the SECURITY SURFACE and therefore the owner's — and it is not a
 * formality: whether a coach may read a client's bodyweight and body-fat series is a consent
 * question, the same shape as V6-9's refusal to let a coach see a client's training footage by
 * accident.
 * ═══════════════════════════════════════════════════════════════════════════════════════════
 */

type Loaded = {
  name: string;
  /** `profiles.avatar_url` — the head's avatar photo when the athlete set one (pro web step 7). */
  photoUrl: string | null;
  /** `coach_clients.id` — the RELATIONSHIP's id, which is what the auto-apply switch writes to. */
  linkId: string;
  /** ⚠️ READ FROM THE ROW, never from the session. RLS has already decided this row is one this
   *  coach may see, so its `coach_id` IS the signed-in coach — and taking it from the row means a
   *  writer cannot name a coach the relationship does not belong to. */
  coachId: string;
  /** His 2026-09-10 switch, as the row holds it. Never a guess: `false` here means the column
   *  said false, and the column is `not null`. */
  autoApplyNextWeek: boolean;
  /** W16. **undefined when the column is not in the row** — the migration has not landed. Not
   *  defaulted here on purpose: the control is the thing that knows what an absent column means,
   *  and a default set at the edge of the read is a fallback nobody can see. */
  visibleRange: VisibleRange | undefined;
  /** The coach's own note on this relationship. `undefined` = could not be read (the card says
   *  so); an EMPTY body = no note yet, which is a different fact and the common one. */
  note: CoachNote | undefined;
  /** `coach_clients.status`. Named `linkStatus` because `status` is the state discriminant, and
   *  one identifier holding two concepts is how the last five sessions' defects started. */
  linkStatus: string;
  /** W72 step 4 — the client card: `coach_clients.invited_email` (profiles has no email) and `accepted_at`, the day
   *  the CLIENT accepted, which is when coaching began (never the invitation's date). */
  invitedEmail: string | null;
  acceptedAt: string | null;
  profile: {
    goals: string[];
    trainingDaysPerWeek: number | null;
    sessionLengthMinutes: number | null;
    unitSystem: string | null;
    /** §10.2's standard follows who holds the wheel, and this column records who. */
    usesFullOptimization: boolean;
    weightKg: number | null;
    heightCm: number | null;
    /** The athlete's own recorded injuries, entries read — never "the column is not null",
     *  which was true of an EMPTY list for all 7 live profiles (`lib/injuries.ts`). */
    injuries: InjuryLine[];
    /** `profiles.first_name`, for the photo card's sentence (`Yiannis hasn't uploaded…`). */
    firstName: string | null;
    /** `profiles.timezone` — the IANA name the client's phone last reported, or null when
        nobody has reported one. NULL STAYS NULL all the way to the screen: see `localClock`. */
    timezone: string | null;
    /** W72 step 4 — the Profile tab's engine inputs, read as stored. */
    mainGoals: string[];
    selectedEquipment: string[];
    equipmentAssumed: boolean;
    trainingStartDate: string | null;
    neverTrained: boolean | null;
    trainingBreakMonths: number | null;
  } | null;
  setsPerDay: Map<string, number>;
  measurements: Measurement[];
  /** Named rather than swallowed: "cannot read" and "nothing recorded" are different answers. */
  measurementsError: string | null;
  assignments: Assignment[];
  /** assignment id → `coach_assignments.workout_id`, so the Training tab can read the plan. */
  workoutIds: Record<string, string>;
  /** Every assignment as the unique index sees it — for predicting a duplicate before writing. */
  sources: { id: string; scheduledDateISO: string | null; sourceWorkoutId: string | null }[];
  days: Set<string>;
};

type State =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "missing" }
  | ({ status: "ready" } & Loaded);

/** The panels this page has. Still a radio group rather than a hand-built tablist — see below. */
/* ⚠️ ONE UNION, DEFINED BESIDE THE RULE THAT SWITCHES ON IT. `showsOverviewContext` is
   exhaustive over `ClientTab`, and a second local copy of the list here would let a tab be
   added to the page without the rule refusing to compile — which is the whole mechanism
   that stops the next tab inheriting the overview's furniture. */
type Tab = ClientTab;

/** ⚠️ A TABLE RATHER THAN A NESTED TERNARY. Three panels fitted in a conditional expression;
 *  a fourth is where that shape starts hiding a missing case, and a `Record` over the union
 *  fails to COMPILE when a fifth arrives instead of rendering an empty label. */
/**
 * 🔴 W21 (B) — THE APP'S THREE WORDS, FOR THE THREE TABS IT HAS (2026-09-19).
 *
 * The app's coach client screen carries `Training · Results · About` and nothing else. Ours has
 * seven tabs, and his word was *"exactly as the app"*.
 *
 * ⚠️ **THE OTHER FOUR ARE REORDERED, NOT DELETED, AND THAT IS THE WHOLE DESIGN.** Hiding Tasks,
 * Goals, Coverage and Overview at phone width would leave four surfaces with NO route to them on
 * a phone — the same defect the roster's `Requests` row was re-drawn to avoid, and a worse one,
 * because Tasks is a whole calendar. The strip already scrolls sideways, so the three the app has
 * are placed FIRST, in the app's order, and the rest follow off the edge of the screen: a coach's
 * first look is the app's, and nothing has been taken away.
 *
 * 🔴 **`PHONE_TAB_LABEL` IS GONE, AND ITS DELETION IS THE SECOND PASS'S POINT (2026-09-19).**
 * It relabelled `Metrics` → `Results` and `Settings` → `About` so that THIS strip could be what
 * a phone painted. Fable's read of that sheet: seven controls at a width that paints three is
 * still the desktop. The phone now has its OWN three-control fieldset (`phoneClientNav`) with
 * its own words, and column 2 is not painted at all below 48rem — so a per-tab phone label had
 * no reader left. A table with no reader is the dead-branch shape this repo's codex names, and
 * the ruler that pinned its fallback is re-targeted rather than deleted.
 */

const TAB_LABEL: Record<Tab, string> = {
  week: "Week",
  progress: "Progress",
  profile: "Profile",
};
/* 🔴 W72 STEP 2 — THE SEVEN LABELS BELOW WERE THE COPIED STRIP'S, KEPT AS A RECORD OF WHAT WENT WHERE
   (`clientTabs.LEGACY_TAB`): overview/training/tasks → Week · metrics/goals/coverage → Progress ·
   settings → Profile. */

/* 🔴 NO `Tasks` TAB YET — AND THE REASON FIRST WRITTEN HERE WAS FALSE, so it is corrected rather
   than quietly replaced. It said "we have no coach-assigned task model on the web at all", which
   came from grepping the generated types for a table named `*task*` and finding none. **A task is
   a `coach_workouts` row with `kind = 'task'`**, assigned through the same RPC into the same
   `coach_assignments` — `TaskEditor.tsx` says so in its first line, and this page already renders
   a DAILY TASKS section with an Add-a-task button. A grep that finds nothing is not a measurement
   that something does not exist.
   ⚠️ The tab is still not built tonight, but for an honest reason now: the tasks a coach assigns
   already appear on the Overview beside the week they belong to, and a tab would be a second
   place to look before anything asks for one. The roster's `Tasks 7d` column is back, counted
   APART from training — a daily wrist series is an assignment and is not a workout.
   Their `Food Journal`, `Meal Plan` and `Documents` stay DISCARDED — the map's existing verdicts. */

/* THE QUERY THIS PAGE READS ITS PANEL OUT OF — the hash's own keys, with the DEV-only `?ht-tab=`
   folded on as an alias (`withDevTabAlias`, which the hash beats when both are present).
   ⚠️ IT IS A MODULE-LEVEL FUNCTION because `useQuerySort` subscribes once and lists neither of its
   arguments as dependencies; a closure rebuilt each render would re-subscribe for no benefit.
   ⚠️ `import.meta.env.DEV` is replaced by the literal `false` at build time, so in anything
   shipped this is `routeQuery` and the alias is not in the bundle at all. */
function clientPageQuery(hash: string): Record<string, string> {
  return withDevTabAlias(routeQuery(hash), import.meta.env.DEV ? window.location.search : "");
}

/** This week's Sunday, as a day key — the "after this" of next week. */
function thisSundayKeyOf(today: Date): string {
  return shiftDayKey(new Date(`${mondayKey(today)}T00:00:00Z`), 6);
}

export function Client({ clientId, coachId, builder }: {
  clientId: string; coachId: string;
  /** William's item 4 — the builder over this client's week (`clientWorkoutNew` / `clientWorkout`), drawn by THIS page: it
   *  holds the client's name, which day a copy is on, and the re-read a save needs. Absent = no builder open. */
  builder?: Extract<Route, { name: "clientWorkoutNew" | "clientWorkout" }>;
}) {
  const [state, setState] = useState<State>({ status: "loading" });
  const [today] = useState(() => calendarToday());
  /* W82 step 6 — `Remind me…`: the ONE reminder window, pre-filled with this client; written through the one hook. */
  const [reminderZone] = useState(() => coachZone());
  const [reminderInstant] = useState(() => Date.now());
  const [reminderNow] = useState(() => new Date(reminderInstant));
  const reminders = useReminders(coachId, reminderZone);
  const [reminderTarget, setReminderTarget] = useState<ReminderTarget | null>(null);
  /* Scheduled calls (his 2026-10-10 idea, `docs/CALLS-BRIEF.md`) — `Book a call…` beside `Remind me…`, this client's calls
     under the header; the window is mounted on demand, like the reminder's. */
  const calls = useCalls(coachId, reminderZone);
  const [callTarget, setCallTarget] = useState<CallTarget | null>(null);
  /* 🔴 JOB 3 STEP 5 CUT 3 — THE REMINDER WINDOW IS MOUNTED ON DEMAND (only once `Remind me…` is pressed), not drawn hidden in
     the page with its four fields. A modal that unmounts returns focus nowhere, so the button is focused again once it is gone. */
  const remindButton = useRef<HTMLButtonElement>(null);
  const reminderWasOpen = useRef(false);
  useEffect(() => {
    if (reminderTarget !== null) { reminderWasOpen.current = true; return; }
    if (reminderWasOpen.current) { reminderWasOpen.current = false; remindButton.current?.focus(); }
  }, [reminderTarget]);
  /* W126 — this client's two-week watch (the Clients row's `Watch N`, in words): null until read, [] = nothing watched. */
  const watch = useClientWatch(clientId, today);
  /* 🔴 W30(j) — THE PANEL IS IN THE ADDRESS (2026-09-19). It was a `useState` with a dev-only
     `?ht-tab=` initialiser, so a pasted `#/clients/<id>?tab=exercise` — the link W30(h) had just
     built — opened on **Overview** and the metrics half only took effect after a click.
     ⚠️ THROUGH `useQuerySort`, WHICH SUBSCRIBES TO `hashchange`, never a `useState` initialiser:
     an initialiser runs ONCE and this page does not remount on navigation — it is the backdrop an
     editor window opens over. That exact bug was DRIVEN on 2026-09-18.
     ⚠️ AND THE DEV `?ht-tab=` IS AN ALIAS ONTO THE SAME KEY rather than a second reader, so the
     render batches keep working and there is still only one answer to which panel is open. */
  const [tab, setTabState] = useQuerySort<Tab>(tabFromQuery, clientPageQuery);
  /* ⚠️ `replaceState`, NOT `pushState`, and for the reason the metrics half gives one screen
     down: choosing which part of a client you are reading is not a place you navigated to, so
     Back must still take the coach OFF this client rather than walking them back through seven
     tabs. It also does not fire `hashchange`, so writing cannot loop through the reader above.
     ⚠️ AND IT WRITES THE PANEL ALONE: a panel's own keys belong to the panel that is drawing
     (`clientQuery`'s own rule), so switching to Settings cannot leave `half=exercise` in an
     address that no longer has a metrics half on it. */
  function setTab(next: Tab, panelKeys = "") {
    setTabState(next);
    window.history.replaceState(null, "", `${href({ name: "client", clientId })}${clientQuery(next, panelKeys)}`);
  }
  /* 🔴 JOB 3 STEP 5 (2026-10-09) — THE `+ N tasks` LINE NO LONGER JUMPS TO A SECOND CALENDAR: the Tasks calendar is folded
     into the Training one, and the line opens that day's tasks window there (`DayTasksWindow`). W71's `openTasksOn` (write
     the Tasks section's week into the address, scroll to it) went with the section it scrolled to. */
  /* W4 — the task editor, opened from the overview. ⚠️ Deliberately NOT a route: unlike the
     workout builder it holds a form a coach fills in one sitting and never returns to by link,
     and `backdropRoute`'s own comment reserves the window/address mechanism for an EDITOR whose
     backdrop you edit against. A task has no backdrop it is edited against. */
  /* 🔴 W30(a) — THE DAY A NEW TASK IS FOR, HELD RATHER THAN ASSUMED.
     `null` = no task is being added. It is a DAY KEY and not a boolean, because the boolean is
     exactly what let `day={today}` sit here unnoticed while the calendar beside it paged freely
     (`docs/EVERFIT-MAP.md` §9-ter; the same defect W22(g) fixed on the Training calendar). */
  const [addingTaskOn, setAddingTaskOn] = useState<string | null>(() =>
    // Dev-only, beside `ht-theme` / `ht-view` (main.tsx): `?ht-task=YYYY-MM-DD` opens the task
    // window on that day at mount, so a headless render can show it. `import.meta.env.DEV` is
    // the literal `false` in a build, so production never reads the parameter.
    import.meta.env.DEV ? new URLSearchParams(window.location.search).get("ht-task") : null);
  // Bumped after a write so the page re-reads itself. A screen that writes and then keeps
  // showing what it read beforehand is telling the coach their own change did not happen.
  const [reloadKey, setReloadKey] = useState(0);
  // QUEUE N2c fix 3: and re-reads when the coach comes back to the tab (the client may have trained meanwhile).
  const returned = useReturnTick();
  const heading = useRef<HTMLHeadingElement>(null);
  /* 🔴 W72 STEP 2 — THE COACH'S "SHOW PROPOSALS" SWITCH, stored on their OWN `profiles` row
     (`show_week_proposals`, migration 20260925110000 — measured readable by the coach, never by their
     clients). Off by default: his amendment makes the proposals an ADD-ON for a coach who wants the numbers.
     `null` = not read yet, and the overlay stays off until it is (never on by guess). In dev,
     `?proposals=on|off` in the address pins it for a still. */
  const [showProposals, setShowProposals] = useState<boolean | null>(null);
  const [shifting, setShifting] = useState(false);
  const [shiftError, setShiftError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void supabase.from("profiles").select("show_week_proposals").eq("id", coachId).maybeSingle()
      .then(({ data }) => { if (!cancelled) setShowProposals(data?.show_week_proposals === true); });
    return () => { cancelled = true; };
  }, [coachId]);
  function setProposalsPref(next: boolean) {
    setShowProposals(next);
    void supabase.from("profiles").update({ show_week_proposals: next }).eq("id", coachId)
      .then(({ error }) => { if (error !== null) setShowProposals(!next); });
  }
  const overlayIsOn = overlayOn(showProposals === true, import.meta.env.DEV ? routeQuery(window.location.hash) : {});
  /* NEXT WEEK'S PAIRS AND THEIR COUNTS, read ONCE for the page: the calendar's per-day marks (the head's
     `· 3 changes proposed` total went on his word 2026-10-22). Keyed on the pairs' identity string so a re-render
     is not a re-read. `null` = not read (or a read failed) — never printed as a zero. */
  const nextPairs = useMemo(
    () => (state.status === "ready" ? overlayPairs(state.assignments, state.workoutIds, state.sources, thisSundayKeyOf(today)) : []),
    [state, today],
  );
  const pairsKey = nextPairs.map((p) => `${p.latest}:${p.previous}`).join(",");
  const [proposedByDay, setProposedByDay] = useState<Record<string, number>>({});
  useEffect(() => {
    setProposedByDay({});
    if (nextPairs.length === 0) return;
    let cancelled = false;
    void (async () => {
      const byDay: Record<string, number> = {};
      for (const pair of nextPairs) {
        const n = await readNextWeekChanges((fn, args) => supabase.rpc(fn as never, args as never), [pair]);
        if (cancelled) return;
        if (n === null) return;
        byDay[pair.dayKey] = (byDay[pair.dayKey] ?? 0) + n;
        setProposedByDay({ ...byDay });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the pairs' identity, not the array's
  }, [pairsKey]);

  // ⚠️ FOCUS MOVES ON ARRIVAL, AND NO ROUTER LIBRARY WOULD HAVE DONE THIS EITHER. A client-side
  // navigation replaces the page without the browser moving focus or announcing anything, so a
  // keyboard user is left standing on a link that no longer exists and a screen-reader user is
  // told nothing at all. Moving focus to the heading is the documented remedy. `tabIndex={-1}`
  // makes the heading focusable programmatically without putting it in the tab order.
  useEffect(() => {
    heading.current?.focus();
  }, [clientId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const link = await supabase
        .from("coach_clients")
        .select("id, coach_id, client_id, status, auto_apply_next_week, invited_first_name, invited_last_name, invited_email, accepted_at")
        .eq("client_id", clientId)
        .maybeSingle();

      if (link.error) {
        if (!cancelled) setState({ status: "error", message: link.error.message });
        return;
      }
      // ⚠️ NOT AN ERROR, AND NOT AN EMPTY PAGE. RLS makes "this person is not your client" and
      // "this person does not exist" the SAME observation from here — and that is correct, since
      // telling a coach which strangers exist would be the leak. The screen says the one true
      // thing: this is not a client of yours.
      if (link.data === null) {
        if (!cancelled) setState({ status: "missing" });
        return;
      }

      const [profile, measurements, assignments, sets] = await Promise.all([
        supabase
          .from("profiles")
          .select("first_name, last_name, goals, training_days_per_week, session_length_minutes, unit_system, weight_kg, height_cm, injuries_data, uses_full_optimization, timezone, main_goals, selected_equipment, equipment_assumed, training_start_date, never_trained, training_break_months, avatar_url")
          .eq("id", clientId)
          .maybeSingle(),
        /* 🔴 THE COACH MAY READ THIS, BY A LIVE POLICY RATHER THAN BY THIS QUERY'S OPTIMISM.
           `body_measurements_coach_read` (`20260914130000`) admits a client's ACTIVE, ACCEPTED
           coach — his 2026-09-14 word, tightened the same day from merely `active` to require
           `accepted_at`, the stamp only the client's own accept RPC writes. If that policy ever
           narrows, this returns an empty list and the card prints its honest absence; it cannot
           show somebody else's weight. Bounded to the window the card reads, plus a little. */
        supabase
          .from("body_measurements")
          .select("measured_at, weight_kg, body_fat_percent")
          .eq("user_id", clientId)
          .order("measured_at", { ascending: false })
          .limit(60),
        supabase
          .from("coach_assignments")
          // ⚠️ THE RELATIONSHIP IS HINTED, AND THE GENERATED TYPES ARE WHAT FORCED IT.
          // `coach_assignments` reaches `coach_workouts` TWICE — `workout_id` and
          // `root_workout_id` — so a bare embed does not compile. `docs/WEB-APP-PLAN.md` §3a's
          // mechanism working: a web surface naming a concept ambiguously is a compiler error.
          //
          // `workout_id` is the right half. V7-2's copy-on-assign made every assignment its OWN
          // client-scoped copy, and `root_workout_id` points back at the library template — so
          // naming the root would make a past day's row rename itself whenever the coach edited
          // the template it came from, which is the exact history-rewriting V7-2 shipped to stop.
          // ⚠️ `note` IS NAMED HERE AND THAT IS THE LOAD-BEARING HALF. `v6-12` measured the shape:
          // a rule about a column is worth nothing if the query does not ask for it, and a
          // field can be perfectly described in a type and never fetched — there the decoding
          // key was present while the `select` had dropped the column, and every check stayed
          // green. The Training tab reads and writes this note; without it here it would edit
          // a value that always arrived null.
          // 🔴 `kind` — SAME REASON AS THE ROSTER, AND ITS ABSENCE WAS VISIBLE ON THIS PAGE. A task is a
          // `coach_workouts` row with `kind = 'task'`, so a page that does not ask for it counts his
          // daily wrist series as prescribed training: this page printed `19 / 32` while the roster,
          // which had just learned to read it, printed `19 / 21` FOR THE SAME MAN. Two screens of one
          // app disagreeing about one number is the defining bug class of this repo, and the fix is
          // one rule (`isTaskAssignment`) that every reader asks — not a second copy of the test.
          // W137 (R7): `day_position` and `assigned_at` — the order of two workouts on one day (his "the top one first").
          // `task_optional` (2026-10-08): the optional kind — the rule is worth nothing if the query does not ask for it.
          .select("id, workout_id, root_workout_id, scheduled_date, day_position, assigned_at, hidden_from_client, note, coach_workouts!workout_id(name, kind, task_optional)")
          .eq("client_id", clientId)
          .order("scheduled_date", { ascending: false }),
        // 🔴 THE TRAINING SIGNAL, AND IT IS `set_logs` FOR THREE MEASURED REASONS — see the
        // header of `client.ts`. In short: `completed_at` on a session means "pressed Finish"
        // (7 rows against 52 that say `status='completed'`); an exercise row means "opened it"
        // (84 sessions have exercise rows and zero logged sets); and 73 of the sets belong to
        // sessions with a NULL `scheduled_date`, so a session-derived date ALONE would lose six days
        // of somebody's training outright. W112: the scheduled day is read beside the set (one
        // embed) and wins when it exists; the instant stays the fallback for exactly those rows.
        supabase
          .from("set_logs")
          // W112: the set and its session's scheduled day — a training day is the day it was scheduled for.
          .select(SET_DAY_SELECT)
          .eq("user_id", clientId)
          .not("completed_at", "is", null),
      ]);

      /* ⚠️ THE MEASUREMENTS' ERROR IS NOT FATAL TO THE PAGE, and that is a deliberate asymmetry.
         A coach who cannot read a weight should still get the training screen; a coach who cannot
         read the ASSIGNMENTS has no page at all. The card says what happened rather than the page
         disappearing around it. */
      const firstError = profile.error ?? assignments.error ?? sets.error;
      if (firstError) {
        if (!cancelled) setState({ status: "error", message: firstError.message });
        return;
      }

      // Both mappings live in `client.ts` and are EXECUTED by `client.test.ts`. They were inline
      // here first, which made them the only fallible code on this page with no runner — and the
      // embedded-name narrowing has a real trap in it that a screen would hide as blank text.
      const rows = toAssignments(assignments.data ?? []);
      const logged = toLoggedSets(sets.data ?? []);
      const workoutIds = Object.fromEntries(
        (assignments.data ?? []).map((a) => [a.id, a.workout_id]),
      );
      // `coalesce(root_workout_id, workout_id)` — the SAME expression
      // `coach_assignments_one_source_per_day_idx` is keyed on, so the screen predicts the real
      // refusal rather than a plausible one of its own.
      // ⚠️ THE `id` IS CARRIED SO A ROW CAN BE EXCLUDED FROM ITS OWN COLLISION TEST — without it
      // `checkReschedule` finds every assignment colliding with itself and no day could ever be
      // moved. `AssignPanel` takes the same list and ignores the extra field.
      const sources = (assignments.data ?? []).map((a) => ({
        id: a.id,
        scheduledDateISO: a.scheduled_date,
        sourceWorkoutId: a.root_workout_id ?? a.workout_id,
      }));

      const p = profile.data;
      if (!cancelled) {
        setState({
          status: "ready",
          linkId: link.data.id,
          photoUrl: p?.avatar_url ?? null,
          coachId: link.data.coach_id,
          autoApplyNextWeek: link.data.auto_apply_next_week,
          visibleRange: await readVisibleRange(link.data.id),
          note: COACH_NOTES_ENABLED ? await readCoachNote(link.data.id) : undefined,
          name: displayName(
            // The two fields the name is made of — never a fabricated roster row. See
            // `displayName`'s own note: a parameter wider than the function's appetite makes
            // every new field a breaking change for callers that do not care about it.
            { firstName: p?.first_name ?? null, lastName: p?.last_name ?? null },
            {
              firstName: link.data.invited_first_name,
              lastName: link.data.invited_last_name,
              email: link.data.invited_email,
            },
          ),
          linkStatus: link.data.status,
          invitedEmail: link.data.invited_email,
          acceptedAt: link.data.accepted_at,
          profile: p
            ? {
                goals: p.goals ?? [],
                usesFullOptimization: p.uses_full_optimization === true,
                trainingDaysPerWeek: p.training_days_per_week,
                sessionLengthMinutes: p.session_length_minutes,
                unitSystem: p.unit_system,
                /* ⚠️ `profiles.weight_kg` AND `height_cm` ARE `numeric` TOO, AND THIS HALF FAILS
                   QUIETLY RATHER THAN LOUDLY. `formatWeight`/`formatHeight` guard on
                   `Number.isFinite(kg)`, which is FALSE for the string `"76.50"` — so they return
                   null and the profile renders **no weight and no height for a client who has
                   both**. No error, no blank page, just two facts missing. The Metrics tab's
                   crash was the same wire being louder about it. */
                weightKg: wireNumber(p.weight_kg),
                heightCm: wireNumber(p.height_cm),
                injuries: readInjuries(p.injuries_data),
                firstName: p.first_name,
                /* ⚠️ NO `?? ""` AND NO FALLBACK TO THE COACH'S OWN ZONE. An absent zone is the
                   honest answer and the card prints no clock at all for one — the coach's time
                   under their client's name is the one output worse than silence here. */
                timezone: p.timezone,
                mainGoals: p.main_goals ?? [],
                selectedEquipment: p.selected_equipment ?? [],
                equipmentAssumed: p.equipment_assumed === true,
                trainingStartDate: p.training_start_date,
                neverTrained: p.never_trained,
                trainingBreakMonths: p.training_break_months,
              }
            : null,
          /* HOW MANY SETS ON EACH DAY — from the same `set_logs` rows the `days` set is built
             from, so the feed and the week strip can never disagree about what a day holds. */
          setsPerDay: setsPerTrainedDay(logged),
          measurements: (measurements.data ?? []).map((r) => ({
            measuredAtISO: r.measured_at,
            /* `numeric` arrives as a STRING over the wire — `76.50`, not 76.5. Coerced once here
               rather than at each reader, and `null` stays `null` (a missing weight is not 0 kg).
               ⚠️ NOW THROUGH THE SHARED `wireNumber`, not a local `Number(…)`. This file KNEW the
               rule and `ClientResults` did not — two statements of one fact, and the one that did
               not know it blanked a whole tab. `Number("abc")` is also NaN, which `wireNumber`
               turns into null; the local form would have passed it on. */
            weightKg: wireNumber(r.weight_kg),
            bodyFatPercent: wireNumber(r.body_fat_percent),
          })),
          measurementsError: measurements.error?.message ?? null,
          assignments: rows,
          workoutIds,
          sources,
          days: trainingDays(logged, clientId),
        });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [clientId, reloadKey, returned]);

  /* W72 STEP 4 — the Overview's feed and assignment list went with the counters they served; the one window the
     Progress tiles read (`last30`) is still derived here, by `client.windows`, the one reader. */
  const view = useMemo(() => {
    if (state.status !== "ready") return null;
    return { w: windows(state.assignments, state.days, today) };
  }, [state, today]);

  /* W72 step 7 — THE LEFT CLIENTS COLUMN IS GONE (Fable's ruling, 2026-09-25): the rail and the roster are the way back
     to another client, so a second client list beside this one's page was a copy of their surface (W20(d)) with nothing of
     its own to say. `<Workspace>` without a list draws no column 2. */

  /* THE WEEK ON SCREEN, in one sentence — Monday to Sunday of the week `today` falls in, from the same two
     facts the calendar draws (the assignments and the days a set was logged). ⚠️ TASKS ARE EXCLUDED: his
     daily wrist series is an assignment, and folding tasks in made every rest day "assigned" then "missed".
     W72 step 2: ONE function, read by the phone's identity row AND the Week tab's summary line. */
  function thisSundayKey(): string {
    return thisSundayKeyOf(today);
  }
  /** Whether any TRAINING is written after this Sunday — "next week exists". */
  function hasNextWeek(s: Loaded): boolean {
    const sunday = thisSundayKey();
    return s.assignments.some((a) => !a.isTask && a.scheduledDateISO !== null && (dayKey(a.scheduledDateISO) ?? "") > sunday);
  }
  function thisWeekSummary(s: Loaded): string {
    const monday = mondayKey(today);
    const sunday = shiftDayKey(new Date(monday + "T00:00:00Z"), 6);
    const week = s.assignments
      .filter((a) => {
        if (a.scheduledDateISO === null || a.hiddenFromClient) return false;
        const d = dayKey(a.scheduledDateISO);
        return d !== null && d >= monday && d <= sunday;
      })
      .map((a) => outcome(a, s.days, today))
      .filter((o) => o !== "task" && o !== "notShown")
      .map((o) => ({ done: o === "trained", missed: o === "missed" }));
    return weekSummaryLine(week);
  }
  /* 🔴 JOB 3 STEP 5 CUT 2 — AN EMPTY WEEK IS SAID INSIDE THE CALENDAR, under this week's own row, never as a loose line above
     it. Null = the week has training, and the head draws its strip. */
  function emptyWeekLineOf(s: Loaded): string | null {
    const summary = thisWeekSummary(s);
    // `weekSummaryLine` says its empty sentence for an empty week and only then — ONE function, asked, never re-derived.
    return summary === weekSummaryLine([]) ? summary : null;
  }
  /* 🔴 HIS WORD 2026-10-22: the head's `4 of 6 done · 3 changes proposed` line went — "use the same days first letter
     thing … from the clients page". The SAME rule (`weekCells`) and the SAME component (`WeekLine`) as the Clients row,
     fed the way `rosterData.ts` feeds it (training by day, tasks apart, optional tasks never owed), so the two pages
     cannot mark one day two ways. A hidden row never reached the client and is not drawn against them. */
  function thisWeekCells(s: Loaded): WeekCell[] {
    const assigned = new Map<string, string[]>();
    const tasks = new Map<string, string[]>();
    for (const a of s.assignments) {
      if (a.scheduledDateISO === null || a.hiddenFromClient || (a.isTask && a.isOptional)) continue;
      const d = dayKey(a.scheduledDateISO);
      if (d === null) continue;
      const into = a.isTask ? tasks : assigned;
      into.set(d, [...(into.get(d) ?? []), a.workoutName ?? (a.isTask ? "Task" : "Workout")]);
    }
    return weekCells(today, assigned, s.days, 0, tasks);
  }
  /** Cut 2 — the week head is drawn only when it has something to say (its summary line, the shift action, a refusal, the
   *  no-next-week line): with an empty week's sentence moved into the calendar, an empty head would leave a blank band. */
  function weekHeadHasContent(s: Loaded): boolean {
    return emptyWeekLineOf(s) === null
      || (s.linkStatus === "active" && shiftPlan(s.assignments, mondayKey(today), s.days).length > 0)
      || shiftError !== null || (overlayIsOn && !hasNextWeek(s));
  }

  return (
    <Workspace>
    {addingTaskOn !== null && state.status === "ready" && state.coachId !== null && (
      <TaskEditor
        clientId={clientId}
        coachId={state.coachId}
        dayKey={addingTaskOn}
        onSaved={() => setReloadKey((n) => n + 1)}
        onClose={() => setAddingTaskOn(null)}
      />
    )}
    <main id="main" className="client-page">
      {/* ══ W21 (B) — THE APP'S TOP BAR AND IDENTITY BLOCK, PHONE ONLY (2026-09-19) ══
          The app's coach client screen opens with a NAV BAR — the client's name centred, a `+`
          at the right — and then one identity row: `EC · Example Client · "Nothing assigned this
          week"`. Ours opened with `← All clients`, a `CLIENT` eyebrow, the name in display type
          and a wrap of goal chips: four lines of head before the first thing a coach came for.
          ⚠️ **BOTH NODES ARE ABSENT BY DEFAULT AND TURNED ON INSIDE THE BAND.** The desktop head
          is untouched; these are drawn only where the app's is.
          ⚠️ **AND THE NAME IS NOT A SECOND `<h1>`** — `client-name` is the page's heading and the
          ref that every navigation focuses. These are `<p>` and `<span>`, so the document has one
          heading at both widths and a screen reader is not told the name three times. */}
      <div className="client-topbar">
        {/* 🔴 THE BACK LINK IS INSIDE THE BAR AND THE DESKTOP DOES NOT KNOW IT MOVED.
            `.client-topbar` is `display: contents` above the band, so it has NO BOX AT ALL and
            the `<p>` lays out exactly where it laid out before — which is why this wrapper could
            be added without touching the desktop. At phone width it becomes the app's nav row.
            ⚠️ ONE NODE FOR ONE DESTINATION: the alternative was a second back link inside a
            phone-only bar, and this screen has already paid for a duplicated route once. */}
        <p className="client-back">
          {/* 🔴 ONE LINK, TWO SPELLINGS, AND EXACTLY ONE ACCESSIBLE NAME AT EACH WIDTH. The app's
              nav bar carries a bare chevron; the desktop carries the words. `display: none` takes
              the unused span out of the accessibility tree with it, so this is never read as
              "‹ All clients All clients". The phone's glyph is `aria-hidden` and the words beside
              it are `ht-visually-hidden` — a chevron alone announces nothing at all, and a back
              control whose name is a punctuation mark is the §12 defect we measured in theirs. */}
          <a className="ht-link" href={href({ name: "roster" })}>
            <span className="client-back-wide">← All clients</span>
            <span className="client-back-glyph" aria-hidden="true">‹</span>
            <span className="client-back-said ht-visually-hidden">All clients</span>
          </a>
        </p>
        {/* ⚠️ `aria-hidden` ON THE NAME ALONE. `client-name` below is the page's `<h1>` and the
            ref every navigation focuses; a screen reader hearing the name here as well would
            hear it twice before the page's heading. The BUTTON beside it is NOT hidden — it is
            a real control and carries its own name. */}
        <span className="client-topbar-name" aria-hidden="true">
          {state.status === "ready" ? state.name : ""}
        </span>
        {/* 🔴 THE APP'S `+` ASSIGNS, AND IT IS WIRED — W22(g) is this repo's own lesson about a
            `+` that was documented as *"the cheapest falsifiable 'better than Everfit'"* and had
            no caller. Pressing it selects the Training tab and moves focus to the assign panel's
            own workout field, which is the first thing a coach fills in. It is offered only on an
            ACTIVE link, the same condition every other assigning control on this page uses: work
            assigned to somebody who has not accepted is work nobody will ever see. */}
        {state.status === "ready" && state.linkStatus === "active" && (
          <button type="button" className="client-topbar-add"
                  onClick={() => {
                    /* W63 — the same "Assign a workout" window the calendar's disc opens, for
                       TODAY, by its address; the form this used to focus is gone. */
                    window.location.hash = `${href({ name: "client", clientId })}${withPicker({ tab: "week" }, dayKeyOf(today))}`.slice(1);
                  }}>
            <span aria-hidden="true">+</span>
            <span className="ht-visually-hidden">Assign work to {state.name}</span>
          </button>
        )}
      </div>

      {/* 🔴 W63 (2026-09-24) — THEIR HEADER: the client's avatar and name on one row (EVERFIT-MAP
          §2-ter, re-read live the same morning). The goal chips and the `CLIENT` eyebrow are gone
          from here; the goals are one card over, in the profile, where theirs are. */}
      <header className="page-head client-head">
        <div className="client-head-row">
          {state.status === "ready" && (
            <Avatar name={state.name} photoUrl={state.photoUrl} className="client-head-avatar" />
          )}
          <h1 className="ht-title client-name" ref={heading} tabIndex={-1}>
            {state.status === "ready" ? state.name : "Client"}
          </h1>
          {state.status === "ready" && (
            <button type="button" className="ht-button client-remind" ref={remindButton}
                    onClick={() => setReminderTarget({ mode: "new", clientId })}>
              Remind me…
            </button>
          )}
          {state.status === "ready" && CALLS_INCLUDED && (
            <button type="button" className="ht-button client-book-call"
                    onClick={() => setCallTarget({ mode: "book", clientId, clientName: state.name, day: null })}>
              Book a call…
            </button>
          )}
        </div>
      </header>
      {state.status === "ready" && (
        <ClientCalls state={calls.state} clientId={clientId} clientName={state.name} now={reminderNow} zone={reminderZone}
                     onOpen={setCallTarget} />
      )}

      {/* 🔴 W63 — THE TABS RUN ACROSS THE TOP OF THE CONTENT, AS THEIRS DO (§2-ter: `Overview ·
          Training · Tasks · Metrics …` under the name). They were a column of radios in the list
          column beside the client rail, and on a scrolled page the two sticky blocks slid over
          each other — text on text, measured in the "before" still. Still REAL RADIOS, for the
          reason written on the strip itself; only where it is drawn moved. The phone keeps its
          own three-control strip below, and this one is `display: none` at that width. */}
      {state.status === "ready" && (
      <fieldset className="client-tabs client-tabs-top">
        <legend className="ht-overline client-tabs-legend">Show</legend>
        {/* ⚠️ THE ORDER IS DERIVED FROM `CLIENT_TABS`, NOT TYPED AGAIN. A second hand-written list
            is how a tab gets added to the type, labelled, and never rendered — and the compiler
            cannot see the omission because the array was `as const` and internally consistent.
            🔴 IT WAS `Object.keys(TAB_LABEL)` UNTIL W30(j), WHICH IS THE SAME ARGUMENT WITH ONE
            MORE READER: the query values a pasted `?tab=` may hold have to come from the same
            membership, and `clientTabs.ts` now holds it once — the union is READ OFF the array,
            so `TAB_LABEL` cannot be short a word and this strip cannot be short a control. */}
        {CLIENT_TABS.map((t) => (
          <label key={t} className={`client-tab ht-caption client-tab-${t}`}>
            <input type="radio" name="client-tab" checked={tab === t} onChange={() => setTab(t)} />
            {TAB_LABEL[t]}
          </label>
        ))}
      </fieldset>
      )}

      {/* ══ W21 (B) — THE APP'S IDENTITY ROW: avatar · name · the week in one sentence ══
          `EC · Example Client · "Nothing assigned this week"` is the whole of the app's head, and
          the sentence is `ClientDetailView.weekSummary` — which counts THE WEEK ON SCREEN rather
          than a rolling window, because the week on screen is what the coach is looking at.
          ⚠️ PHONE ONLY, and the name here is `aria-hidden` for the same reason the bar's is: the
          `<h1>` above is the page's one heading.
          ⚠️ THE SENTENCE IS **NOT** `aria-hidden` — it is the only place this figure is said at
          phone width, since the Overview's week cards do not render here. */}
      {state.status === "ready" && (
        <div className="client-idblock">
          <Avatar name={state.name} photoUrl={state.photoUrl} className="client-idblock-avatar" />
          <span className="client-idblock-name" aria-hidden="true">{state.name}</span>
          {/* W72 step 7 (Fable, finding 9): on the WEEK tab the summary line under the tabs already says this figure, so
              the identity row does not say it twice; on Progress and Profile it is the only place it is said. */}
          {tab !== "week" && (
            <span className="client-idblock-week">
              {thisWeekSummary(state)}
            </span>
          )}
        </div>
      )}

      {/* ══ W21 (B), SECOND PASS — THE APP'S THREE-TAB STRIP, UNDER THE IDENTITY ROW ══
          🔴 A SECOND NODE, AND THE REASON IS STRUCTURAL RATHER THAN A PREFERENCE. The desktop's
          strip lives in column 2, which is a SIBLING of `<main>` — so an ordering rule can put it
          above the page or below it and never BETWEEN two of main's own children, which is where
          the app puts it (nav bar → identity row → tabs). The first pass recorded that as
          *"ours differs"*; Fable's ruling on the library sheet answers it, so the phone gets its
          own control HERE and column 2's is `display: none` at this width.
          ⚠️ **ITS OWN RADIO `name`, never the desktop group's.** Two fieldsets sharing one name
          are ONE group to the browser, so checking a phone radio would silently uncheck a desktop
          radio React still believes is checked — two writers of one fact, one of them invisible.
          ⚠️ **THREE CONTROLS, NOT SEVEN, AND NOTHING LOSES ITS ADDRESS** — `About` is a GROUP:
          Overview, Tasks, Goals and Coverage are rows inside its panel, and the About control
          stays chosen while a coach is standing on one of them (`phoneGroup`). */}
      {state.status === "ready" && (
        <fieldset className="client-tabs-phone">
          <legend className="ht-visually-hidden">Which part of this client</legend>
          {PHONE_STRIP.map((g) => (
            <label key={g} className="client-tab-p ht-caption">
              <input type="radio" name="client-tab-phone"
                     checked={phoneGroup(tab) === g}
                     onChange={() => setTab(tabForGroup(g))} />
              <span>{PHONE_GROUP_LABEL[g]}</span>
            </label>
          ))}
        </fieldset>
      )}

      {/* THE WEEK LINE as the masthead — docs/WEB-DESIGN-DIRECTION.md §2. Cells from the loaded
          assignments (by name, hidden ones excluded) and the days a set was logged.
          🔴 THE OVERVIEW'S, AND ONLY THE OVERVIEW'S. This read `tab !== "training"` until the
          2026-09-19 render measured what that meant: Metrics and Settings each drew this strip
          plus the standing headline above their own content, putting their first heading at
          y ≈ 680 of a 1000 px viewport. `showsOverviewContext` states the rule once and is
          exhaustive over the tab union, so a new tab must be named rather than inheriting this.
          Training stays out for the stronger reason it always had: its calendar's first row IS
          this week, and two drawings of one week a screenful apart is the duplication §2b
          refuses. */}
      {/* 🔴 W63 — THE WEEK STRIP AND THE "WHAT NEEDS DOING" BANNER LEFT THE OVERVIEW. Theirs has
          neither (§2-bis/§2-ter: the board is the whole Overview), and ours was the page's
          heaviest part: the banner read "3 sessions not trained … Thu 27 Aug" above "Trained
          yesterday". The week is the Training tab's first row; filed in EVERFIT-MAP §6b. */}

      {state.status === "loading" && (
        <p className="ht-callout loading-line" aria-live="polite">
          Loading…
        </p>
      )}

      {state.status === "missing" && (
        <p className="ht-callout client-empty">
          This is not one of your clients. If you have just invited them, they will appear here
          once they accept.
        </p>
      )}

      {state.status === "error" && (
        <div className="client-error" role="alert">
          {/* The server's own words — usually a policy or a network fact worth seeing. */}
          <p className="ht-body">Could not load this client.</p>
          <p className="ht-callout">{state.message}</p>
        </div>
      )}

      {state.status === "ready" && view !== null && (
        <>
          {/* ══ W21 (B), SECOND PASS — THE WAY BACK OUT OF AN **About** ROW, PHONE ONLY ══
              Overview, Tasks, Goals and Coverage are rows inside About at this width, so a coach
              who opened one is INSIDE About and needs the row out. The strip cannot be it: the
              About control is already chosen (`phoneGroup`), so pressing it fires no `change`
              event at all — a radio you are already on is not a control.
              ⚠️ It is a real `<button>` and it is `display: none` above the band, so the desktop,
              which shows all seven tabs at once and is never inside anything, never meets it. */}


          {/* The banner goes with the week it summarises, so it lives exactly where that week is
              drawn — the same rule, the same function, never a second condition that can drift. */}

          {/* 🔴 W72 STEP 2 FIX 1 — THE WEEK HEAD IS ONE LINE UNDER THE TABS: since 2026-10-22 (his word) the Clients row's M–S
              strip, no longer `4 of 6 done · 3 changes proposed`, with the missed-week action as a
              TERTIARY text action at its right. The auto-apply switch MOVED to the Profile tab's NEXT WEEK
              rows (still mounted ONCE — `tab` holds one value); the overlay's switch is on the calendar bar.
              ⚠️ THE SHIFT: every TRAINING day from this Monday on moves a week later, written LATEST FIRST
              (`shiftBlock.ts` says why; `tools/w72-shift-block/rehearse.sql` proves it on the live rows,
              rolled back: earliest-first is refused 23505, latest-first succeeds). */}
          {/* 🔴 W126 — THE WATCH, IN WORDS, FIRST ON THE WEEK TAB (the tab the Clients row opens): one quiet line per
              movement skipped / left unlogged two weeks running, only when there is one. The row says `Watch N`. */}
          {tab === "week" && watch !== null && watch.length > 0 && (
            <section aria-labelledby="client-watch-h" className="client-watch">
              <h2 id="client-watch-h" className="ht-overline client-watch-label">Watch</h2>
              <ul className="client-watch-list">
                {watch.map((w) => <li key={w.key}>{watchItemLine(w)}</li>)}
              </ul>
            </section>
          )}

          {tab === "week" && weekHeadHasContent(state) && (
            <section aria-label="This week" className="client-week-head">
              <div className="client-week-line">
                {emptyWeekLineOf(state) === null && (
                  <WeekLine cells={thisWeekCells(state)} label={`${state.name}'s week`} />
                )}
                {state.linkStatus === "active" && (() => {
                  const plan = shiftPlan(state.assignments, mondayKey(today), state.days);
                  if (plan.length === 0) return null;
                  return (
                    <button type="button" className="ours-text-action client-week-shift" disabled={shifting}
                            onClick={() => {
                              if (!window.confirm(shiftConfirm(plan))) return;
                              setShifting(true); setShiftError(null);
                              void (async () => {
                                for (const m of plan) {
                                  const r = await rescheduleAssignment(supabase as unknown as AssignmentWriter, m.id, m.to, shortDate(m.to));
                                  if (!r.ok) { setShiftError(r.said); break; }
                                }
                                setShifting(false);
                                setReloadKey((k) => k + 1);
                              })();
                            }}>
                      {shifting ? "Moving…" : "Shift this block forward a week"}
                    </button>
                  );
                })()}
              </div>
              {shiftError !== null && <p className="ht-caption" role="alert">{shiftError}</p>}
              {overlayIsOn && !hasNextWeek(state) && (
                <p className="ht-caption client-week-nonext">{NO_NEXT_WEEK_LINE}</p>
              )}
            </section>
          )}

          {tab === "week" && (
            <ClientTraining
              clientId={clientId}
              clientName={state.name}
              overlay={overlayIsOn ? { pairs: nextPairs, counts: proposedByDay } : undefined}
              overlayToggle={{ on: overlayIsOn, disabled: showProposals === null, onToggle: setProposalsPref }}
              onOverlayWritten={() => setReloadKey((k) => k + 1)}
              assignments={state.assignments}
              assignmentWorkoutIds={state.workoutIds}
              unitSystem={state.profile?.unitSystem ?? null}
              trainingDays={state.days}
              today={today}
              existingSources={state.sources}
              onChanged={() => setReloadKey((k) => k + 1)}
              /* Job 3 step 5 — the task doors on the ONE calendar, offered only on an ACTIVE link: a task assigned to
                 somebody who has not accepted is work nobody will ever see. */
              onAddTask={state.linkStatus === "active" ? setAddingTaskOn : undefined}
              emptyWeekLine={emptyWeekLineOf(state) ?? undefined}
            />
          )}

          {/* 🔴 JOB 3 STEP 5 — W20(c)'s TASKS CALENDAR IS GONE FROM HERE: it drew the Training calendar's grid a second
              time (`Today ‹ range › 1·2·4 weeks` and seven `+` a week, again). A day's tasks are its cell's `+ N tasks` line
              on the ONE calendar above; the task list is still the data (`kind = 'task'` rows in `coach_assignments`). */}

          {/* 🔴 W72 STEP 4 — PROGRESS IS ONE COMPONENT: tiles · lifts and holds on the app's own chart · goals · the
              coverage strip (Proposal 4, his chart note). The old Metrics / Goals / Coverage panels and the Overview
              counters step 2 parked here are redesigned into it; the full goal report and the full map open from it. */}
          {tab === "progress" && state.status === "ready" && view !== null && (
            <ClientProgress clientId={clientId} today={today}
                            goals={state.profile?.goals ?? null}
                            usesFullOptimization={state.profile?.usesFullOptimization ?? false}
                            measurements={state.measurements} measurementsError={state.measurementsError}
                            last30={view.w.last30} firstName={state.profile?.firstName ?? null} />
          )}


          {/* 🔴 W72 STEP 4 — PROFILE IS ONE COMPONENT (Proposal 4): the engine's inputs as rows, NEXT WEEK's switch (its one
              mount), the client card and `End coaching…`. The private-notes card is composed here because its save is. */}
          {tab === "profile" && state.status === "ready" && (
            <ClientProfile clientId={clientId} name={state.name} photoUrl={state.photoUrl} profile={state.profile} linkId={state.linkId}
                           linkStatus={state.linkStatus} autoApplyNextWeek={state.autoApplyNextWeek}
                           visibleRange={state.visibleRange} invitedEmail={state.invitedEmail} acceptedAt={state.acceptedAt}
                           days={state.days} onChanged={() => setReloadKey((k) => k + 1)}
                           notes={COACH_NOTES_ENABLED ? (
              <CoachNotesCard
                  note={state.note}
                  onSave={async (body) => {
                    /* ⚠️ UPSERT, KEYED ON THE RELATIONSHIP. The row is created on the first save
                       and never seeded — so a coach who writes nothing leaves no row, and the
                       `on conflict` target is the primary key the table was built around.
                       ⚠️ AND `updated_at` IS SENT rather than left to the column default, because
                       a DEFAULT only fires on INSERT: an UPDATE that did not name it would leave
                       the footer reporting the day the note was FIRST written, for ever. The value
                       that comes back is the SERVER'S, read from the returning row — the card
                       never invents "saved just now". */
                    const { data, error } = await supabase
                      .from("coach_client_notes")
                      .upsert({
                        coach_client_id: state.linkId,
                        body,
                        updated_at: new Date().toISOString(),
                      }, { onConflict: "coach_client_id" })
                      .select("body, updated_at")
                      .single();
                    if (error !== null) {
                      return { error: `That note did not save: ${error.message}` };
                    }
                    return {
                      saved: {
                        linkId: state.linkId,
                        body: data.body,
                        updatedAtISO: data.updated_at,
                      },
                    };
                  }}
                />
                           ) : null} />
          )}


          {/* 🔴 W72 STEP 2 — THE OVERVIEW BOARD DISSOLVES INTO THE THREE TABS: each card below is gated on
              the tab it now belongs to (Week · Progress · Profile), in place, so nothing a coach used
              loses its home. The board's three-column grid becomes a stack (`is-stacked`); step 4
              redesigns Progress and Profile as whole pages. */}
          {(
          /* 🔴 THE THREE-COLUMN BOARD — theirs, measured live (EVERFIT-MAP §2-bis), in our tokens.
             §7 called this screen a vertical stack and it is not; building it as one would have
             been a redesign wearing a copy's name.
             ⚠️ THE DOM ORDER IS THE READING ORDER, and the columns are placed by CSS. Under 1024
             the grid collapses to one column and this order is what a phone gets — so the wide
             column's content comes first, which is what a coach opens this page to see. */
          <div className="client-board is-stacked">
          {/* ⚠️ THREE REAL COLUMN WRAPPERS, NOT `grid-column` ON SIX SIBLINGS — and the
              difference is a hole in the page. An item given an explicit column but no
              explicit ROW still flows by DOM row order, so the right-hand card landed on
              row 5 and left the top third of the board empty. MEASURED in the live page:
              all six WERE grid children with the right `grid-column-start`, which is why
              reading the CSS did not show it. A board is three columns; saying so in the
              DOM also makes the stacked order right — column 1 entire, then 2, then 3. */}
          <div className="client-col client-col-wide">





          </div>
          <div className="client-col client-col-mid">

            {/* JOB 3 STEP 5 — W4's DAILY TASKS section went: its one control, `Add a task today`, is the calendar bar's
                (once on the page, not twice). */}




          </div>
          <div className="client-col client-col-side">
          </div>
          </div>
          )}
        </>
      )}
      {reminderTarget !== null && (
        <ReminderWindow target={reminderTarget} onClose={() => setReminderTarget(null)}
                        clients={reminders.state.status === "ready" ? reminders.state.clients : []}
                        write={reminders.write} zone={reminderZone} now={reminderNow} />
      )}
      {callTarget !== null && (
        <CallWindow target={callTarget} onClose={() => setCallTarget(null)} write={calls.write} zone={reminderZone} now={reminderNow} />
      )}
      {CALLS_INCLUDED && <CallHost onEnded={calls.write.ended} />}
      {/* 🔴 WILLIAM'S ITEM 4 — THE BUILDER IN THE CLIENT'S WEEK, as a window over this page (which stays mounted, so its week
          re-reads on a save and does not move). A pencil's copy opens only when it IS one of this client's days: a pasted
          id from somebody else's week never opens under this client's name. */}
      {builder !== undefined && state.status === "ready" && (() => {
        const onDay = builder.name === "clientWorkout"
          ? state.assignments.find((a) => state.workoutIds[a.id] === builder.workoutId) : undefined;
        if (builder.name === "clientWorkout" && onDay === undefined) return null;
        return (
          <WorkoutBuilder key={builder.name === "clientWorkout" ? builder.workoutId : `new:${builder.day}`}
                          workoutId={builder.name === "clientWorkout" ? builder.workoutId : null} route={builder}
                          client={{ clientId, clientName: state.name,
                                    day: builder.name === "clientWorkoutNew" ? builder.day : onDay?.scheduledDateISO ?? null }}
                          onSaved={() => setReloadKey((n) => n + 1)} />
        );
      })()}
    </main>
    </Workspace>
  );
}
